import { Category, CategoryType, SectionBase, Skin } from '@wix/add-panel-common';
import { TextLabel } from '@wix/wix-base-ui';
import classnames from 'classnames';
import React, { FC, Fragment, memo, useCallback, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Errors } from '../constants/Errors';
import { FedopsInteraction } from '../constants/FedopsInteraction';
import { isDebug } from '../utils/env';
import { ActionsDispatcherProvider, useActionsDispatcher } from './ActionsDispatcher';
import type { UseActionsDispatcher } from './ActionsDispatcher/ActionsDispatcher.types';
import cssStyles from './AddPanel.css';
import type { AddPanelInnerProps, AddPanelOuterProps, MouseActionHandlers } from './AddPanel.types';
import addPanelStyles from './AddPanelNewWorkspace.scss';
import { CategoryView } from './CategoryView';
import type { CategoryViewRef } from './CategoryView/CategoryView.types';
import { CurrentCategoryProvider } from './CurrentCategoryProvider';
import { DebugFlagsProvider } from './DebugFlagsProvider';
import { ErrorBoundary } from './ErrorBoundary';
import { useAddPanelHover } from './hooks/useAddPanelHover';
import { useDispatchCategoryOpen, useDispatchSectionOpen } from './hooks/useDispatchOpen';
import { usePanelNavigation } from './hooks/usePanelNavigation';
import { useGetScrollBIParams } from './hooks/useGetScrollBIParams';
import { LocaleProvider } from './LocaleProvider';
import { MyWidgetsProvider } from './MyWidgetsProvider';
import { OnErrorProvider } from './OnErrorProvider';
import { RelatedAppsProvider } from './RelatedAppsProvider';
import { SavedComponentsProvider } from './SavedComponentsProvider';
import { SearchButton } from './SearchButton';
import { SkinProvider } from './Skin';
import {
  getCategoryAndGroups,
  getTranslationIfNeeded,
  getCategoryFirstSection,
  getFirstSectionIdFromCategoryGroups,
} from './utils';
import { useUtils, UtilsProvider } from './UtilsProvider';

const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });

const categoryLabelEllipsisProps = { marginTop: 8 };

const AddPanelInner: FC<AddPanelInnerProps> = memo<AddPanelInnerProps>(
  ({
    categories,
    isEditorSearchButtonVisible,
    pathsState = null,
    getExternalSectionComponent,
    deepLinkPath: deepLinkPathFromProps = null,
    dataVersion,
  }) => {
    const [addPanelSearchQuery, setAddPanelSearchQuery] = useState<string>('');
    const {
      dispatchFedopsInteractionStarted,
      dispatchFedopsInteractionEnded,
      dispatchCategoryClick,
      dispatchSectionClick,
      dispatchUnmount,
    } = useActionsDispatcher();

    const { translate } = useUtils();

    useEffect(() => {
      if (categories.length === 0) {
        throw new Error(Errors.EmptyCategories);
      }
    }, [categories]);

    const categoryViewRef = useRef<CategoryViewRef>(null);
    const { path, setPath, deepLinkPath, shouldUseDeepLinkPath, pathsStateController, scrollPreventionController } =
      usePanelNavigation({
        scrollActorRef: categoryViewRef,
        deepLinkPath: deepLinkPathFromProps,
        pathsState,
        categories,
      });

    const [selectedCategory, groups] = getCategoryAndGroups(
      categories,
      deepLinkPath && shouldUseDeepLinkPath ? deepLinkPath[0] : path[0],
    );
    const firstSection = getCategoryFirstSection(selectedCategory);

    const selectedSectionId = addPanelSearchQuery
      ? ''
      : deepLinkPath && shouldUseDeepLinkPath
      ? deepLinkPath[1]
        ? deepLinkPath[1]
        : firstSection.systemId
      : path[1] || firstSection.systemId;

    const selectedCategoryId = selectedCategory.systemId;
    const lastSelectedCategoryIdRef = useRef(selectedCategoryId);

    const dispatchUnmountRef = useRef<UseActionsDispatcher['dispatchUnmount']>(dispatchUnmount);

    const addPanelHoverTimeoutIdRef = useRef<number>();

    const categoriesListContainerRef = useRef<HTMLDivElement>(null);
    const categoriesListRef = useRef<HTMLUListElement>(null);
    const searchButtonRef = useRef<HTMLButtonElement>(null);

    const getScrollBIParams = useGetScrollBIParams({
      categories,
      categoriesListContainerRef,
      categoriesListRef,
      searchButtonRef,
      isEditorSearchButtonVisible,
    });

    const { enter, leave } = useAddPanelHover();

    const openSection = useCallback(
      (sectionId: string, shouldScroll = true) => {
        if (shouldScroll && categoryViewRef.current) {
          categoryViewRef.current.scrollToSectionTop(sectionId, (top: number) => {
            pathsStateController.updateCategory(selectedCategoryId, sectionId, top);
          });
        }

        if (selectedSectionId === sectionId) {
          return;
        }

        // when we scrolled content to next section it updates path and this very callback is called and visible lag is present
        // so when we open section we need to prevent next auto scroll to it with path update
        scrollPreventionController.preventNextAutoScroll();

        if (addPanelSearchQuery) {
          setAddPanelSearchQuery('');
        }
        setPath([selectedCategoryId, sectionId]);
      },
      [selectedSectionId, scrollPreventionController, setPath, selectedCategoryId, pathsStateController],
    );

    const openCategory = useCallback(
      (category: Category) => {
        if (!categoryViewRef.current) {
          return;
        }

        const { systemId: categorySystemId } = category;

        const isScrollInProcess = categoryViewRef.current.isScrollInProcess();
        const categoriesEqual = selectedCategoryId === categorySystemId;

        if (isScrollInProcess || categoriesEqual) {
          return;
        }

        lastSelectedCategoryIdRef.current = selectedCategoryId;
        const [, nextCategoryGroups] = getCategoryAndGroups(categories, categorySystemId);
        const nextSectionId =
          pathsStateController.getCategory(categorySystemId)?.section ||
          getFirstSectionIdFromCategoryGroups(nextCategoryGroups);

        pathsStateController.updateCategory(
          selectedCategoryId,
          selectedSectionId,
          categoryViewRef.current.getScrollTop(),
        );
        if (addPanelSearchQuery) {
          setAddPanelSearchQuery('');
        }
        setPath([categorySystemId, nextSectionId]);

        dispatchFedopsInteractionStarted({ interaction: FedopsInteraction.TabChange });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedCategoryId, categories, selectedSectionId, categoryViewRef, setPath, pathsStateController],
    );

    const handleScroll = useCallback(
      (sectionId: string, top: number) => {
        if (!selectedSectionId) {
          return;
        }
        // when category selected it's content changed and it may have smaller height
        // because of height differences onScroll event is fired with new category, but with old sectionId :(
        // so we need to prevent this onScroll when category change
        if (lastSelectedCategoryIdRef.current !== selectedCategoryId) {
          lastSelectedCategoryIdRef.current = selectedCategoryId;
          return;
        }

        openSection(sectionId, false);
        pathsStateController.updateCategory(selectedCategoryId, sectionId, top);
      },
      [openSection, selectedCategoryId, pathsStateController, selectedSectionId],
    );

    const getMouseActionsHandlers = (category: Category): MouseActionHandlers => {
      const { systemId: categorySystemId, id: categoryId } = category;

      return {
        onMouseEnter: (event) => {
          enter(event, () => {
            if (selectedCategoryId !== categorySystemId) {
              window.clearTimeout(addPanelHoverTimeoutIdRef.current);
              addPanelHoverTimeoutIdRef.current = window.setTimeout(() => {
                // TODO
                // self.notifySelectedCategoryEvent('ADD_MENU_CATEGORY_SELECT', category);
              }, 3000);
            }
            openCategory(category);
          });
        },
        onMouseLeave: (event) => {
          leave(event, categorySystemId);
        },
        onClick: () => {
          dispatchFedopsInteractionStarted({ interaction: FedopsInteraction.CategoryClick });
          // TODO
          // self.notifySelectedCategoryEvent('ADD_MENU_CATEGORY_CLICK', category);

          const biParams = getScrollBIParams(categoryId);

          dispatchCategoryClick({
            categoryId,
            biParams,
          });

          openCategory(category);

          dispatchFedopsInteractionEnded({ interaction: FedopsInteraction.CategoryClick });
        },
      };
    };

    const getMouseActionsHandlersSections = (category: Category, section: SectionBase): MouseActionHandlers => {
      const { systemId: sectionSystemId } = section;

      return {
        onMouseEnter: (event) => {
          if (addPanelSearchQuery) {
            return;
          }
          enter(
            event,
            () => {
              if (selectedSectionId !== sectionSystemId) {
                window.clearTimeout(addPanelHoverTimeoutIdRef.current);
                addPanelHoverTimeoutIdRef.current = window.setTimeout(() => {
                  // TODO
                  // self.notifySelectedSubCategoryEvent(
                  //   'ADD_MENU_SUB_CATEGORY_SELECT',
                  //   self.categories[self.state.selectedIndex],
                  //   section,
                  // );
                }, 1500);
              }
              openSection(sectionSystemId);
            },
            true,
          );
        },
        onMouseLeave: (event) => {
          if (addPanelSearchQuery) {
            return;
          }
          leave(event, sectionSystemId);
        },
        onClick: () => {
          dispatchSectionClick({ categoryId: selectedCategory.id, sectionName: section.sectionName });
          openSection(sectionSystemId);
        },
      };
    };

    const scrollBiParams = getScrollBIParams(selectedCategory.id);
    useDispatchCategoryOpen(selectedCategory, selectedSectionId, scrollBiParams);
    useDispatchSectionOpen(selectedCategory, selectedSectionId);

    useEffect(() => {
      dispatchUnmountRef.current = dispatchUnmount;
    }, [dispatchUnmount]);

    useEffect(
      () => () => {
        if (!dispatchUnmountRef.current) {
          return;
        }

        dispatchUnmountRef.current({
          pathsState: pathsStateController.getPathsState(),
        });
      },
      [pathsStateController],
    );

    const categoriesList = (
      <>
        {isEditorSearchButtonVisible && <SearchButton buttonRef={searchButtonRef} />}

        <ul
          ref={categoriesListRef}
          className={classnames(addPanelStyles.addPanelCategoryList)}
          data-hook="add-panel-categories-list"
        >
          {categories.map((category) => {
            const {
              systemId,
              type,
              id,
              translations: { label },
            } = category;
            const isSelected = systemId === selectedCategoryId;
            const isCategoryDriver = type === CategoryType.CategoryDivider;
            if (isCategoryDriver) {
              return (
                <li key={id} className={classnames(addPanelStyles.category, id, addPanelStyles.categoryDivider)}>
                  <hr className={addPanelStyles.categoryDividerInner} />
                </li>
              );
            }

            return (
              <li key={systemId} className={classnames(addPanelStyles.category, id)}>
                <span
                  className={classnames(addPanelStyles.categoryNameWrapper, {
                    [addPanelStyles.isSelected]: isSelected,
                  })}
                  data-hook={`add-panel-category-name-wrapper-${id}`}
                  data-state-is-selected={isSelected}
                  {...getMouseActionsHandlers(category)}
                >
                  <TextLabel
                    className={addPanelStyles.categoryName}
                    ellipsisProps={categoryLabelEllipsisProps}
                    type="T07"
                    value={translate(label)}
                    shouldTranslate={false}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      </>
    );

    const sectionsList = (
      <ul className={classnames(addPanelStyles.addPanelSectionsList)} data-hook="add-panel-sections-list">
        {groups.map(
          ({ sections, translations: { shouldTranslate, title }, systemId: groupSystemId, noDivider }, index) => (
            <Fragment key={groupSystemId}>
              {index > 0 && (!noDivider || groups[index - 1].bottomDivider) && (
                <li className={classnames(selectedCategory?.id, addPanelStyles.sectionDivider)}>
                  <hr className={addPanelStyles.groupDivider} />
                </li>
              )}

              {title && (
                <li
                  className={classnames(
                    selectedCategory?.id,
                    addPanelStyles.sectionHeader,
                    noDivider && cssStyles.sectionHeaderNoDivider,
                  )}
                >
                  <TextLabel
                    type="T21"
                    className={addPanelStyles.categoryGroupTitle}
                    value={getTranslationIfNeeded(title, translate, shouldTranslate)}
                    shouldTranslate={false}
                  />
                </li>
              )}

              {sections.map((section: SectionBase) => {
                const {
                  automationId,
                  systemId: sectionSystemId,
                  translations: { subNavigationTitle, shouldTranslate },
                } = section;
                const isSelected = sectionSystemId === selectedSectionId;

                return (
                  <li key={sectionSystemId} className={classnames(addPanelStyles.category, selectedCategory?.id)}>
                    <span
                      className={classnames(addPanelStyles.categoryNameWrapper, {
                        [addPanelStyles.isSelected]: isSelected,
                      })}
                      data-hook={`${automationId}-nav`}
                      data-state-is-selected={isSelected}
                      {...getMouseActionsHandlersSections(selectedCategory, section)}
                    >
                      <TextLabel
                        className={addPanelStyles.categoryName}
                        ellipsisProps={categoryLabelEllipsisProps}
                        type="T07"
                        value={getTranslationIfNeeded(subNavigationTitle, translate, shouldTranslate)}
                        shouldTranslate={false}
                      />
                    </span>
                  </li>
                );
              })}
            </Fragment>
          ),
        )}
      </ul>
    );

    return (
      <>
        <div
          className={classnames(addPanelStyles.addPanelCategoriesContainer, {
            [addPanelStyles.withSearch]: isEditorSearchButtonVisible,
          })}
          data-hook="add-panel-categories-list-container"
          ref={categoriesListContainerRef}
        >
          {categoriesList}
          {isDebug() ? <div className={addPanelStyles.newLabel}>new panel: {dataVersion}</div> : null}
        </div>

        {selectedCategory?.hasSubcategory && sectionsList}

        {selectedCategory && (
          <CurrentCategoryProvider category={selectedCategory}>
            <CategoryView
              addPanelSearchQuery={addPanelSearchQuery}
              setAddPanelSearchQuery={setAddPanelSearchQuery}
              ref={categoryViewRef}
              category={selectedCategory}
              selectedSectionId={selectedSectionId}
              getExternalSectionComponent={getExternalSectionComponent}
              onScroll={handleScroll}
            />
          </CurrentCategoryProvider>
        )}
      </>
    );
  },
);

export const AddPanel: FC<AddPanelOuterProps> = memo<AddPanelOuterProps>(
  ({
    onAction,
    onError,
    locale,
    utils,
    savedComponents,
    myWidgets,
    relatedApps,
    skin = Skin.Classic,
    ...otherProps
  }) => (
    <OnErrorProvider onError={onError}>
      <DebugFlagsProvider>
        <LocaleProvider locale={locale}>
          <UtilsProvider utils={utils}>
            <QueryClientProvider client={queryClient}>
              <ActionsDispatcherProvider onAction={onAction}>
                <ErrorBoundary>
                  <SavedComponentsProvider savedComponents={savedComponents}>
                    <RelatedAppsProvider relatedApps={relatedApps}>
                      <MyWidgetsProvider myWidgets={myWidgets}>
                        <SkinProvider skin={skin}>
                          <AddPanelInner {...otherProps} />
                        </SkinProvider>
                      </MyWidgetsProvider>
                    </RelatedAppsProvider>
                  </SavedComponentsProvider>
                </ErrorBoundary>
              </ActionsDispatcherProvider>
            </QueryClientProvider>
          </UtilsProvider>
        </LocaleProvider>
      </DebugFlagsProvider>
    </OnErrorProvider>
  ),
);
